import { nanoid } from 'nanoid'
import { z } from 'zod'

import { defaultFooter, defaultFooterCSS } from '../footer'
import { defaultGuide, guideSchema } from './guide'
import { defaultReference, referenceSchema } from './reference'
import { headerLinkSchema, getHeaderLinks } from '../header-links'

import { nanoidSchema } from '@/utility'
import { SidebarType } from '@/project/version/sidebar'

export const versionSchema = z
  .object({
    uid: nanoidSchema,
    name: z.string().default('Default Version'),
    primaryGuideId: z.string(),
    primaryReferenceId: z.string(),
    references: z.record(z.string(), referenceSchema),
    guides: z.record(z.string(), guideSchema),
    header: headerLinkSchema.array(),
    footer: z.string().default(''),
    footerCss: z.string().default(''),
    footerBelowSidebar: z.boolean().default(false),
    headScript: z.string().default(''),
    bodyScript: z.string().default(''),
  })
  .transform((v) => {
    return {
      ...v,
      header: getHeaderLinks({
        guides: v.guides,
        references: v.references,
        header: v.header,
      }),
    }
  })

export type Version = z.output<typeof versionSchema>

export function defaultVersion(
  initialState: Partial<Version> = {},
  options: {
    hideGuide?: boolean
    hideReference?: boolean
  } = {},
) {
  const guide = defaultGuide()
  const reference = defaultReference()

  if (options.hideGuide) guide.show = false
  if (options.hideReference) reference.show = false

  return versionSchema.parse({
    uid: nanoid(),
    name: 'Version 0',
    primaryGuideId: guide.uid,
    primaryReferenceId: reference.uid,
    references: { [reference.uid]: reference },
    guides: { [guide.uid]: guide },
    header: getHeaderLinks({
      guides: { [guide.uid]: guide },
      references: { [reference.uid]: reference },
      header: [],
    }),
    footer: defaultFooter,
    footerCss: defaultFooterCSS,
    footerBelowSidebar: false,
    headScript: '',
    bodyScript: '',
    ...initialState,
  } satisfies Version)
}

/** Extracts all yjs ids from a version */
export function getAllYjsIds(version: Version) {
  const documentByUid: Record<string, string> = {}
  const referenceByUid: Record<string, string> = {}

  Object.values(version.references).forEach((reference) => {
    referenceByUid[reference.uid] = reference.yjsReference
  })

  Object.values(version.guides).forEach((guide) => {
    Object.values(guide.sidebar.items).forEach((item) => {
      if (item.type === SidebarType.Page) {
        documentByUid[item.uid] = item.yjsReference
      }
    })
  })

  return {
    /** Map of guide page uid to yjs document id */
    documentByUid: documentByUid,
    /** Map of reference uid to yjs reference id */
    referenceByUid: referenceByUid,
  }
}
